import { generateUUID } from '../utils/uuid';
/**
 * GenericShell — Generic EHR / Clinical Workstation simulation.
 * Demonstrates how RAAPID integrates with any EHR system for point-of-care.
 * No vendor branding — neutral professional chrome.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MARGARET, DOROTHY,
  GENERIC_VITALS, GENERIC_CONDITIONS, GENERIC_MEDICATIONS, GENERIC_LABS, GENERIC_NOTES, GENERIC_ORDERS, GENERIC_CHART_DOCS,
  DOROTHY_VITALS, DOROTHY_CONDITIONS, DOROTHY_MEDICATIONS, DOROTHY_LABS, DOROTHY_NOTES, DOROTHY_ORDERS, DOROTHY_CHART_DOCS,
} from './data/genericDummy';
import { MOCK_PROVIDERS, type MockProvider } from './data/mockProviders';
import type { EHRCondition, EHRWriteBackEvent, AddendumEntry } from '../types';

const EHR_SYNC_CHANNEL = 'raapid-ehr-sync';

// ─── Patient registry ────────────────────────────────────────────────────────

const PATIENTS = {
  'CRN-PAT-00482': {
    info: MARGARET,
    vitals: GENERIC_VITALS, conditions: GENERIC_CONDITIONS,
    meds: GENERIC_MEDICATIONS, labs: GENERIC_LABS,
    notes: GENERIC_NOTES, orders: GENERIC_ORDERS,
    chartDocs: GENERIC_CHART_DOCS,
    apptTime: '9:00 AM', status: 'In Room',
  },
  'CRN-PAT-00715': {
    info: DOROTHY,
    vitals: DOROTHY_VITALS, conditions: DOROTHY_CONDITIONS,
    meds: DOROTHY_MEDICATIONS, labs: DOROTHY_LABS,
    notes: DOROTHY_NOTES, orders: DOROTHY_ORDERS,
    chartDocs: DOROTHY_CHART_DOCS,
    apptTime: '10:30 AM', status: 'Waiting',
  },
} as const;

type PatientId = keyof typeof PATIENTS;
const TABS = ['Summary', 'Chart Review', 'Orders', 'Results', 'Notes', 'RAAPID Coding Intel'];

let conditionIdCounter = 0;
function nextConditionId() { return `cond-${++conditionIdCounter}`; }

function toEHRConditions(conditions: readonly { icd: string; display: string; status: string; onset: string }[]): EHRCondition[] {
  return conditions.map(c => ({ id: nextConditionId(), icd: c.icd, display: c.display, status: c.status as 'Active' | 'Resolved', onset: c.onset, source: 'ehr' as const }));
}
const G = {
  dark: '#111827',
  nav: '#1F2937',
  mid: '#374151',
  accent: '#3B82F6',
  accentLight: '#60A5FA',
  text: '#9CA3AF',
  lightBg: '#F3F4F6',
  bg: '#F9FAFB',
  border: '#4B5563',
};

export default function GenericShell() {
  const navigate = useNavigate();
  const [selectedProvider, setSelectedProvider] = useState<MockProvider | null>(null);
  const [patientId, setPatientId] = useState<PatientId>('CRN-PAT-00482');
  const [activeTab, setActiveTab] = useState('Chart Review');
  const [rightOpen, setRightOpen] = useState(true);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [notifOpen, setNotifOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  // ── Mutable EHR state (synced from RAAPID iframes) ──────────────────────
  const [conditions, setConditions] = useState<EHRCondition[]>(() => toEHRConditions(PATIENTS['CRN-PAT-00482'].conditions));
  const [addendumEntries, setAddendumEntries] = useState<AddendumEntry[]>([]);
  const [addendumFinalized, setAddendumFinalized] = useState(false);
  const [attestationInfo, setAttestationInfo] = useState<{ by: string; at: string; specialty: string | null } | null>(null);
  const ehrChannelRef = useRef<BroadcastChannel | null>(null);
  const finalizedRef = useRef(false);
  const [toasts, setToasts] = useState<{ id: string; text: string; type: 'success' | 'info' }[]>([]);

  const addToast = (text: string, type: 'success' | 'info' = 'success') => {
    const id = generateUUID();
    setToasts(prev => [...prev, { id, text, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
  };
  finalizedRef.current = addendumFinalized;

  // Listen for write-back events from RAAPID iframes
  useEffect(() => {
    const ch = new BroadcastChannel(EHR_SYNC_CHANNEL);
    ehrChannelRef.current = ch;
    ch.onmessage = (e: MessageEvent<EHRWriteBackEvent>) => {
      const msg = e.data;
      if (msg.patientId !== patientId) return;
      if (finalizedRef.current && (msg.type === 'code-accepted' || msg.type === 'code-rejected')) return;
      if (msg.type === 'code-accepted' && msg.code) {
        const sid = msg.code.suggestionId;
        setConditions(prev => {
          if (prev.some(c => c.icd === msg.code!.icd && c.source === 'raapid')) return prev;
          return [...prev, { id: nextConditionId(), icd: msg.code!.icd, display: msg.code!.description, status: 'Active', onset: new Date().toISOString().slice(0, 10), source: 'raapid' }];
        });
        setAddendumEntries(prev => {
          if (prev.some(e => e.suggestionId === sid)) return prev;
          return [...prev, { icd: msg.code!.icd, description: msg.code!.description, hcc: msg.code!.hcc, gapType: msg.code!.gapType, evidence: msg.code!.evidence, providerName: msg.code!.providerName, timestamp: msg.code!.timestamp, action: 'accepted', suggestionId: sid }];
        });
        addToast(`${msg.code!.icd} added to problem list`, 'success');
      } else if (msg.type === 'code-rejected' && msg.code) {
        const sid = msg.code.suggestionId;
        setAddendumEntries(prev => {
          if (prev.some(e => e.suggestionId === sid)) return prev;
          return [...prev, { icd: msg.code!.icd, description: msg.code!.description, hcc: msg.code!.hcc, gapType: msg.code!.gapType, evidence: msg.code!.evidence, providerName: msg.code!.providerName, timestamp: msg.code!.timestamp, action: 'deferred', rejectionReason: msg.rejectionReason, suggestionId: sid }];
        });
      } else if (msg.type === 'code-undone' && msg.code) {
        const sid = msg.code.suggestionId;
        setConditions(prev => prev.filter(c => !(c.icd === msg.code!.icd && c.source === 'raapid')));
        setAddendumEntries(prev => prev.filter(e => e.suggestionId !== sid));
        addToast(`${msg.code!.icd} removed from problem list`, 'info');
      } else if (msg.type === 'attested') {
        setAddendumFinalized(true);
        setAttestationInfo({ by: msg.attestation?.by ?? 'Provider', at: msg.attestation?.at ?? new Date().toISOString(), specialty: msg.attestation?.specialty ?? null });
        addToast('Risk adjustment review attested', 'success');
      }
    };
    return () => ch.close();
  }, [patientId]);

  const filteredPatients = Object.entries(PATIENTS).filter(([, p]) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return p.info.name.toLowerCase().includes(q)
      || p.info.mrn.toLowerCase().includes(q)
      || p.info.encounter.toLowerCase().includes(q);
  });

  const searchOpen = searchQuery.trim().length > 0;

  const handleSearch = (value: string) => {
    setSearchQuery(value);
  };

  const handleMessage = useCallback((e: MessageEvent) => {
    if (e.origin === window.location.origin && e.data?.raapid === 'openFull') setActiveTab('RAAPID Coding Intel');
  }, []);
  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  const switchPatient = (id: PatientId) => {
    setPatientId(id);
    setScheduleOpen(false);
    setSearchQuery('');
    setConditions(toEHRConditions(PATIENTS[id].conditions));
  };

  const pt = PATIENTS[patientId];
  const { info } = pt;
  const providerSpecialty = selectedProvider?.specialty ?? 'PCP';
  const providerNameParam = encodeURIComponent(selectedProvider?.name ?? '');
  const compactUrl = `${window.location.origin}${import.meta.env.BASE_URL}compact?mode=mock&theme=generic&patientId=${patientId}&specialty=${providerSpecialty}&providerName=${providerNameParam}`;
  const panelUrl   = `${window.location.origin}${import.meta.env.BASE_URL}app?mode=mock&theme=generic&patientId=${patientId}&specialty=${providerSpecialty}&providerName=${providerNameParam}`;

  if (!selectedProvider) {
    return <ProviderPicker theme={G} onSelect={setSelectedProvider} onBack={() => navigate('/')} />;
  }

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', fontSize: '13px', background: G.bg, overflow: 'hidden' }}>

      {/* ── Top nav ──────────────────────────────────────────────────────── */}
      <div style={{ background: G.dark, height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 14px', flexShrink: 0, borderBottom: `2px solid ${G.nav}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <button onClick={() => navigate('/')} style={ghost} title="Demo home"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg></button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '28px', height: '28px', borderRadius: '6px', background: G.accent, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ color: 'white', fontSize: '14px', fontWeight: 900 }}>+</span>
            </div>
            <span style={{ color: 'white', fontWeight: 700, fontSize: '15px' }}>Clinical Workstation</span>
            <span style={{ color: G.text, fontSize: '11px', marginLeft: '3px' }}>EHR</span>
          </div>
          <div style={{ position: 'relative' }}>
            <div style={{ background: G.nav, border: `1px solid ${searchOpen ? G.accentLight : G.border}`, borderRadius: '4px', padding: '4px 10px', display: 'flex', alignItems: 'center', gap: '6px', width: '260px', transition: 'border-color 0.15s' }}>
              <span style={{ color: G.text, fontSize: '11px' }}>🔍</span>
              <input
                value={searchQuery}
                onChange={e => handleSearch(e.target.value)}
                placeholder="Search patient chart..."
                style={{ background: 'transparent', border: 'none', outline: 'none', color: '#D1D5DB', fontSize: '11px', width: '100%' }}
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} style={{ background: 'none', border: 'none', color: G.text, cursor: 'pointer', fontSize: '12px', padding: 0, lineHeight: 1 }}>&times;</button>
              )}
            </div>
            {searchOpen && (
              <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 100, background: G.mid, border: `1px solid ${G.border}`, borderRadius: '8px', boxShadow: '0 12px 32px rgba(0,0,0,0.5)', width: '340px', maxHeight: '290px', overflowY: 'auto' }}>
                <div style={{ padding: '6px 12px', background: G.nav, fontSize: '10px', color: '#6B7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', position: 'sticky', top: 0 }}>
                  Search Results · {filteredPatients.length} found
                </div>
                {filteredPatients.length === 0 ? (
                  <div style={{ padding: '20px 14px', textAlign: 'center' }}>
                    <div style={{ color: '#6B7280', fontSize: '12px' }}>No patients match "<span style={{ color: '#D1D5DB' }}>{searchQuery}</span>"</div>
                    <div style={{ color: '#4B5563', fontSize: '11px', marginTop: '4px' }}>Try searching by name, MRN, or encounter type</div>
                  </div>
                ) : (
                  filteredPatients.map(([id, p]) => {
                    const active = id === patientId;
                    return (
                      <button key={id} onClick={() => switchPatient(id as PatientId)} style={{ display: 'flex', width: '100%', alignItems: 'center', gap: '10px', padding: '10px 14px', background: active ? G.accent : 'transparent', border: 'none', borderBottom: `1px solid ${G.border}`, cursor: 'pointer', textAlign: 'left' }}
                        onMouseEnter={e => { if (!active) e.currentTarget.style.background = G.nav; }}
                        onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                      >
                        <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: active ? 'rgba(255,255,255,0.2)' : G.border, display: 'flex', alignItems: 'center', justifyContent: 'center', color: active ? 'white' : G.text, fontSize: '10px', fontWeight: 700, flexShrink: 0 }}>
                          {p.info.name.split(',')[0].slice(0, 2).toUpperCase()}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ color: 'white', fontSize: '12px', fontWeight: 700 }}>{p.info.name}</div>
                          <div style={{ color: active ? 'rgba(255,255,255,0.7)' : G.text, fontSize: '10px', marginTop: '1px' }}>
                            MRN: {p.info.mrn} · {p.info.age} · {p.info.encounter}
                          </div>
                        </div>
                        <span style={{ fontSize: '9px', padding: '2px 6px', borderRadius: '8px', fontWeight: 700, background: p.status === 'In Room' ? '#14532D' : '#78350F', color: p.status === 'In Room' ? '#4ADE80' : '#FCD34D', flexShrink: 0 }}>{p.status}</span>
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Notifications */}
          <div style={{ position: 'relative' }}>
            <button onClick={() => { setNotifOpen(!notifOpen); setProfileOpen(false); }} style={{ background: notifOpen ? G.mid : 'transparent', border: 'none', color: G.accentLight, cursor: 'pointer', fontSize: '14px', padding: '4px 6px', borderRadius: '4px', position: 'relative' }}>
              🔔
              <span style={{ position: 'absolute', top: '0', right: '2px', width: '14px', height: '14px', borderRadius: '50%', background: '#EF4444', color: 'white', fontSize: '8px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>3</span>
            </button>
            {notifOpen && (
              <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 100, background: G.mid, border: `1px solid ${G.border}`, borderRadius: '8px', boxShadow: '0 12px 32px rgba(0,0,0,0.5)', width: '320px', overflow: 'hidden' }}>
                <div style={{ padding: '8px 12px', background: G.nav, fontSize: '11px', color: '#D1D5DB', fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Notifications</span>
                  <span style={{ fontSize: '10px', color: G.accentLight, cursor: 'pointer' }}>Mark all read</span>
                </div>
                {[
                  { icon: '🎯', text: 'RAAPID: 3 new HCC coding gaps detected for Thompson, Margaret E.', time: '10 min ago', unread: true },
                  { icon: '⚠️', text: 'Recaptured Risk: code I25.10 flagged — evidence not found for Thompson, M.', time: '25 min ago', unread: true },
                  { icon: '✅', text: 'Code E11.65 accepted and written back to EHR successfully', time: '1 hr ago', unread: true },
                  { icon: '📊', text: 'RAF score updated: +0.665 projected lift for Williams, Dorothy', time: '2 hrs ago', unread: false },
                ].map((n, i) => (
                  <div key={i} style={{ padding: '10px 12px', borderBottom: `1px solid ${G.border}`, display: 'flex', gap: '10px', alignItems: 'flex-start', background: n.unread ? 'rgba(79,70,229,0.08)' : 'transparent', cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = G.nav}
                    onMouseLeave={e => e.currentTarget.style.background = n.unread ? 'rgba(79,70,229,0.08)' : 'transparent'}
                  >
                    <span style={{ fontSize: '14px', flexShrink: 0, marginTop: '1px' }}>{n.icon}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '11px', color: '#D1D5DB', lineHeight: 1.4 }}>{n.text}</div>
                      <div style={{ fontSize: '10px', color: '#6B7280', marginTop: '2px' }}>{n.time}</div>
                    </div>
                    {n.unread && <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: G.accent, flexShrink: 0, marginTop: '4px' }} />}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Profile */}
          <div style={{ position: 'relative' }}>
            <button onClick={() => { setProfileOpen(!profileOpen); setNotifOpen(false); }} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: profileOpen ? G.mid : 'transparent', border: 'none', cursor: 'pointer', padding: '3px 8px', borderRadius: '6px' }}>
              <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: G.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '10px', fontWeight: 700 }}>{selectedProvider.initials}</div>
              <span style={{ color: '#D1D5DB', fontSize: '12px' }}>{selectedProvider.name}</span>
              <span style={{ color: G.text, fontSize: '10px' }}>{profileOpen ? '▲' : '▼'}</span>
            </button>
            {profileOpen && (
              <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 100, background: G.mid, border: `1px solid ${G.border}`, borderRadius: '8px', boxShadow: '0 12px 32px rgba(0,0,0,0.5)', width: '220px', overflow: 'hidden' }}>
                <div style={{ padding: '12px 14px', borderBottom: `1px solid ${G.border}`, display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: G.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '13px', fontWeight: 700 }}>{selectedProvider.initials}</div>
                  <div>
                    <div style={{ color: 'white', fontSize: '12px', fontWeight: 700 }}>{selectedProvider.name}</div>
                    <div style={{ color: '#6B7280', fontSize: '10px' }}>{selectedProvider.title}</div>
                  </div>
                </div>
                {[
                  { label: 'My Profile', icon: '👤' },
                  { label: 'Preferences', icon: '⚙️' },
                  { label: 'Help & Support', icon: '❓' },
                ].map((item) => (
                  <div key={item.label} style={{ padding: '8px 14px', display: 'flex', alignItems: 'center', gap: '8px', color: '#D1D5DB', fontSize: '12px', cursor: 'pointer', borderBottom: `1px solid ${G.border}` }}
                    onMouseEnter={e => e.currentTarget.style.background = G.nav}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <span style={{ fontSize: '13px' }}>{item.icon}</span>{item.label}
                  </div>
                ))}
                <div style={{ padding: '8px 14px', display: 'flex', alignItems: 'center', gap: '8px', color: '#EF4444', fontSize: '12px', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = G.nav}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  onClick={() => setSelectedProvider(null)}
                >
                  <span style={{ fontSize: '13px' }}>🚪</span>Sign Out
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Schedule bar ─────────────────────────────────────────────────── */}
      <div style={{ background: G.nav, borderBottom: `1px solid ${G.dark}`, padding: '5px 14px', display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0, position: 'relative' }}>
        <span style={{ color: '#6B7280', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', flexShrink: 0 }}>Schedule</span>
        <button onClick={() => setScheduleOpen(!scheduleOpen)} style={{ background: G.mid, border: `1px solid ${G.border}`, borderRadius: '6px', padding: '5px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', flex: 1, maxWidth: '420px' }}>
          <div style={{ textAlign: 'left', flex: 1 }}>
            <div style={{ color: 'white', fontSize: '13px', fontWeight: 700 }}>{pt.info.name}</div>
            <div style={{ color: G.text, fontSize: '11px' }}>{pt.info.encounter} · {pt.apptTime}</div>
          </div>
          <span style={{ fontSize: '10px', padding: '2px 7px', borderRadius: '8px', fontWeight: 700, background: pt.status === 'In Room' ? '#14532D' : '#78350F', color: pt.status === 'In Room' ? '#4ADE80' : '#FCD34D', flexShrink: 0 }}>{pt.status}</span>
          <span style={{ color: G.text, fontSize: '10px' }}>{scheduleOpen ? '▲' : '▼'}</span>
        </button>
        {scheduleOpen && (
          <div style={{ position: 'absolute', top: '100%', left: '14px', zIndex: 50, background: G.mid, border: `1px solid ${G.border}`, borderRadius: '8px', overflow: 'hidden', boxShadow: '0 8px 24px rgba(0,0,0,0.4)', minWidth: '380px' }}>
            <div style={{ padding: '6px 12px', background: G.nav, fontSize: '10px', color: '#6B7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Today's Patient Schedule</div>
            {Object.entries(PATIENTS).map(([id, p]) => {
              const active = id === patientId;
              return (
                <button key={id} onClick={() => switchPatient(id as PatientId)} style={{ display: 'flex', width: '100%', alignItems: 'center', gap: '12px', padding: '10px 14px', background: active ? G.accent : 'transparent', border: 'none', borderBottom: `1px solid ${G.border}`, cursor: 'pointer', textAlign: 'left' }}
                  onMouseEnter={e => { if (!active) e.currentTarget.style.background = G.nav; }}
                  onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ color: 'white', fontSize: '13px', fontWeight: 700 }}>{p.info.name}</div>
                    <div style={{ color: active ? 'rgba(255,255,255,0.7)' : G.text, fontSize: '11px', marginTop: '2px' }}>
                      {p.info.age} · {p.info.encounter} · MRN: {p.info.mrn}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ color: active ? 'rgba(255,255,255,0.8)' : G.accentLight, fontSize: '11px', fontWeight: 600 }}>{p.apptTime}</div>
                    <span style={{ fontSize: '9px', padding: '2px 6px', borderRadius: '8px', fontWeight: 700, background: p.status === 'In Room' ? '#14532D' : '#78350F', color: p.status === 'In Room' ? '#4ADE80' : '#FCD34D' }}>{p.status}</span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Patient bar ──────────────────────────────────────────────────── */}
      <div style={{ background: G.mid, padding: '8px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, borderBottom: `1px solid ${G.dark}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '18px' }}>
          <div>
            <span style={{ color: 'white', fontWeight: 700, fontSize: '15px' }}>{info.name}</span>
            <span style={{ color: '#D1D5DB', fontSize: '13px', marginLeft: '10px' }}>{info.gender} · {info.age} · DOB: {info.dob} · MRN: {info.mrn}</span>
          </div>
          <Sep />
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#4ADE80', display: 'inline-block' }} />
            <span style={{ color: '#D1D5DB', fontSize: '13px' }}>{info.encounter} · {info.date}</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <span style={{ background: '#312E81', border: `1px solid ${G.accentLight}`, color: G.accentLight, padding: '2px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 700 }}>{info.plan}</span>
        </div>
      </div>

      {/* ── Main layout ──────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* Left sidebar */}
        <div style={{ width: '52px', background: G.dark, display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: '8px', gap: '2px', flexShrink: 0, borderRight: `1px solid ${G.nav}` }}>
          {([['📋','Chart','Chart Review'],['📊','Results','Results'],['💊','Meds','Summary'],['⚕️','Orders','Orders'],['📝','Notes','Notes'],['🩺','Problems','Summary']] as const).map(([icon, label, tab]) => {
            const active = activeTab === tab;
            return (
              <button key={label} onClick={() => setActiveTab(tab as string)} style={{ background: active ? G.mid : 'transparent', border: 'none', color: active ? G.accentLight : G.text, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', padding: '7px 2px', borderRadius: '4px', width: '48px', fontSize: '16px' }}
                onMouseEnter={e => { if (!active) e.currentTarget.style.background = G.mid; }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                title={label}
              ><span>{icon}</span><span style={{ fontSize: '9px', color: active ? G.accentLight : G.text }}>{label}</span></button>
            );
          })}
        </div>

        {/* Center content */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Tab bar */}
          <div style={{ display: 'flex', background: G.mid, borderBottom: `2px solid ${G.dark}`, flexShrink: 0, padding: '0 6px' }}>
            {TABS.map((tab) => {
              const active = activeTab === tab;
              return (
                <button key={tab} onClick={() => setActiveTab(tab)} style={{ padding: '9px 16px', border: 'none', background: active ? G.bg : 'transparent', color: active ? G.dark : '#D1D5DB', fontWeight: active ? 700 : 400, fontSize: '13px', cursor: 'pointer', borderRadius: '4px 4px 0 0', display: 'flex', alignItems: 'center', gap: '5px', marginTop: '4px', fontFamily: '-apple-system, sans-serif' }}>
                  {tab}
                </button>
              );
            })}
          </div>

          {/* Content */}
          <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }} key={patientId}>
            {/* RAAPID iframe — always mounted, hidden when other tab active */}
            <div style={{ height: '100%', display: activeTab === 'RAAPID Coding Intel' ? 'flex' : 'none', flexDirection: 'column' }}>
              <div style={{ background: '#EEF2FF', borderBottom: '1px solid #C7D2FE', padding: '4px 12px', display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                <span style={{ background: G.accent, color: 'white', fontSize: '9px', padding: '1px 6px', borderRadius: '8px', fontWeight: 700 }}>ACTIVE</span>
              </div>
              <iframe src={panelUrl} style={{ flex: 1, height: 0, minHeight: 0, border: 'none', width: '100%' }} title="RAAPID Coding Intelligence" allow="clipboard-write" />
            </div>
            {activeTab === 'Summary' ? (
              <SummaryTab
                pt={pt}
                conditions={conditions}
                onSetConditions={setConditions}
                addendumEntries={addendumEntries}
                onConditionRemoved={(c) => {
                  ehrChannelRef.current?.postMessage({
                    type: 'code-undone',
                    patientId,
                    code: { icd: c.icd, description: c.display, hcc: null, gapType: '', evidence: '', providerName: '', timestamp: new Date().toISOString(), suggestionId: '' },
                  } satisfies EHRWriteBackEvent);
                }}
                onConditionRestored={(c) => {
                  ehrChannelRef.current?.postMessage({
                    type: 'code-accepted',
                    patientId,
                    code: { icd: c.icd, description: c.display, hcc: null, gapType: '', evidence: '', providerName: selectedProvider?.name ?? 'Provider', timestamp: new Date().toISOString(), suggestionId: '' },
                  } satisfies EHRWriteBackEvent);
                }}
              />
            ) : activeTab === 'Chart Review' ? (
              <ChartReviewTab docs={pt.chartDocs} notes={pt.notes} />
            ) : activeTab === 'Orders' ? (
              <OrdersTab orders={pt.orders} />
            ) : activeTab === 'Results' ? (
              <ResultsTab labs={pt.labs} />
            ) : activeTab === 'Notes' ? (
              <NotesTab notes={pt.notes} addendumEntries={addendumEntries} addendumFinalized={addendumFinalized} attestationInfo={attestationInfo} providerName={selectedProvider?.name ?? 'Provider'} providerTitle={selectedProvider?.title ?? ''} patientMrn={info.mrn} encounterType={info.encounter} />
            ) : null}
          </div>
        </div>

        {/* Right RAAPID panel */}
        <div style={{ width: rightOpen ? '360px' : '32px', flexShrink: 0, borderLeft: `2px solid ${G.dark}`, display: 'flex', flexDirection: 'column', background: G.dark, transition: 'width 0.2s ease', overflow: 'hidden' }}>
          <div style={{ background: G.accent, padding: '8px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            {rightOpen && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <img src={`${import.meta.env.BASE_URL}raapid-logo.png`} alt="RAAPID" style={{ height: '14px', filter: 'brightness(0) invert(1)' }} />
                <span style={{ color: 'white', fontSize: '11px', fontWeight: 600, opacity: 0.9 }}>Coding Intelligence</span>
              </div>
            )}
            <button onClick={() => setRightOpen(!rightOpen)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', borderRadius: '4px', padding: '2px 6px', cursor: 'pointer', fontSize: '12px', marginLeft: rightOpen ? 0 : 'auto' }}>{rightOpen ? '→' : '←'}</button>
          </div>
          {rightOpen && <iframe key={patientId} src={compactUrl} style={{ flex: 1, height: 0, minHeight: 0, border: 'none', width: '100%', background: '#F9FAFB' }} title="RAAPID Summary" allow="clipboard-write" />}
        </div>
      </div>

      {/* Toast notifications */}
      {toasts.length > 0 && (
        <div style={{ position: 'fixed', bottom: '28px', right: '20px', zIndex: 200, display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {toasts.map(t => (
            <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 14px', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', fontSize: '12px', fontWeight: 600, animation: 'fadeIn 0.2s ease', background: t.type === 'success' ? '#ECFDF5' : '#EFF6FF', color: t.type === 'success' ? '#059669' : '#1D4ED8', border: `1px solid ${t.type === 'success' ? '#A7F3D0' : '#BFDBFE'}` }}>
              <span>{t.type === 'success' ? '✓' : 'ℹ'}</span>
              {t.text}
            </div>
          ))}
        </div>
      )}

      {/* Status bar */}
      <div style={{ background: G.dark, borderTop: `1px solid ${G.nav}`, padding: '2px 14px', display: 'flex', justifyContent: 'space-between', flexShrink: 0 }}>
        <span style={{ color: '#6B7280', fontSize: '10px' }}>Clinical Workstation · EHR Integration Demo</span>
        <span style={{ color: '#6B7280', fontSize: '10px' }}>Session: {new Date().toLocaleTimeString()} · {selectedProvider.name} · {selectedProvider.title}</span>
      </div>
    </div>
  );
}

// ─── Tab content ──────────────────────────────────────────────────────────────

// ─── Gap type label/color helpers ────────────────────────────────────────────

const GAP_LABELS: Record<string, { label: string; bg: string; color: string }> = {
  suspect_new:     { label: 'Suspecting Code Gap',   bg: '#DBEAFE', color: '#1D4ED8' },
  opportunity:     { label: 'Recaptured Opportunity', bg: '#D1FAE5', color: '#059669' },
  recapture:       { label: 'Recaptured Existing', bg: '#EDE9FE', color: '#7C3AED' },
  recapture_stale: { label: 'Suspecting Emerging', bg: '#FEF3C7', color: '#B45309' },
  over_claimed:    { label: 'Recaptured Risk', bg: '#FEE2E2', color: '#DC2626' },
};


// ─── Addendum text generator ─────────────────────────────────────────────────

function gapBadge(gapType: string) {
  const g = GAP_LABELS[gapType] ?? GAP_LABELS.suspect_new;
  return { label: g.label, bg: g.bg, color: g.color };
}

function generateAddendumText(entries: AddendumEntry[], providerName: string, providerTitle: string, mrn: string, encounterType: string, finalized: boolean, attestation: { by: string; at: string; specialty: string | null } | null): string {
  const today = new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  const lines: string[] = [
    'RISK ADJUSTMENT CODING ADDENDUM',
    '─'.repeat(40),
    `Date: ${today}`,
    `Provider: ${providerName} | ${providerTitle}`,
    `Encounter: ${encounterType} | MRN: ${mrn}`,
    '',
    'CONDITIONS ADDRESSED:',
  ];
  const accepted = entries.filter(e => e.action === 'accepted');
  const deferred = entries.filter(e => e.action === 'deferred');
  for (const e of accepted) lines.push(`  [ACCEPTED]  ${e.icd} — ${e.description}${e.hcc ? `  |  ${e.hcc}` : ''}`);
  for (const e of deferred) lines.push(`  [DEFERRED]  ${e.icd} — ${e.description}${e.rejectionReason ? `  (${e.rejectionReason})` : ''}`);
  if (accepted.length > 0) {
    lines.push('', 'CLINICAL BASIS:');
    for (const e of accepted) {
      lines.push(`  • ${e.icd}: ${e.description}`);
      if (e.evidence) lines.push(`    Supporting: ${e.evidence}`);
    }
  }
  if (finalized && attestation) {
    const signTime = new Date(attestation.at).toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    lines.push('', 'ATTESTATION:', '  I have reviewed the above diagnoses and supporting clinical', '  evidence. Accepted conditions are supported by current', '  documentation and clinical findings from this encounter.', '', `  ✓ Electronically signed: ${attestation.by}${attestation.specialty ? ` (${attestation.specialty})` : ''}`, `    ${signTime}`);
  } else {
    lines.push('', '[ DRAFT — Pending attestation ]');
  }
  return lines.join('\n');
}

// ─── Summary Tab ─────────────────────────────────────────────────────────────

interface SummaryTabProps {
  pt: typeof PATIENTS[PatientId];
  conditions: EHRCondition[];
  onSetConditions: React.Dispatch<React.SetStateAction<EHRCondition[]>>;
  onConditionRemoved?: (condition: EHRCondition) => void;
  onConditionRestored?: (condition: EHRCondition) => void;
  addendumEntries: AddendumEntry[];
}

function SummaryTab({ pt, conditions, onSetConditions, onConditionRemoved, onConditionRestored, addendumEntries }: SummaryTabProps) {
  return (
    <div style={{ height: '100%', overflowY: 'auto', padding: '14px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', alignContent: 'start' }}>
      <Card title="Latest Vitals" sub={pt.vitals[0].date}>
        <VR label="Blood Pressure" value={pt.vitals[0].bp} alert={parseInt(pt.vitals[0].bp) > 140} />
        <VR label="Heart Rate" value={pt.vitals[0].hr} />
        <VR label="SpO₂" value={pt.vitals[0].spo2} alert={parseInt(pt.vitals[0].spo2) < 95} />
        <VR label="Weight / BMI" value={`${pt.vitals[0].wt} / BMI ${pt.vitals[0].bmi}`} alert={parseFloat(pt.vitals[0].bmi) < 19 || parseFloat(pt.vitals[0].bmi) > 30} />
        <VR label="Temp / RR" value={`${pt.vitals[0].temp} / ${pt.vitals[0].rr}`} />
      </Card>
      <ActiveConditionsCard conditions={conditions} onSetConditions={onSetConditions} onConditionRemoved={onConditionRemoved} onConditionRestored={onConditionRestored} />
      <Card title="Current Medications" sub={`${pt.meds.length} active`}>
        {pt.meds.map((m, i) => (
          <div key={i} style={{ padding: '5px 0', borderBottom: '1px solid #F3F4F6' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#111827' }}>{'name' in m ? m.name : ''}</div>
            <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '1px' }}>{'sig' in m ? m.sig : ''}</div>
          </div>
        ))}
      </Card>
      <Card title="Encounter" sub={pt.info.date}>
        <VR label="Type" value={pt.info.encounter} /><VR label="Physician" value={pt.info.physician} /><VR label="Insurance" value={pt.info.plan} />
      </Card>
    </div>
  );
}


// ─── Active Conditions Card (Full CRUD) ──────────────────────────────────────

function ActiveConditionsCard({ conditions, onSetConditions, onConditionRemoved, onConditionRestored }: { conditions: EHRCondition[]; onSetConditions: React.Dispatch<React.SetStateAction<EHRCondition[]>>; onConditionRemoved?: (condition: EHRCondition) => void; onConditionRestored?: (condition: EHRCondition) => void }) {
  // Bug fix #3/#8: use stable ID instead of index for editing
  const [editId, setEditId] = useState<string | null>(null);
  const [editIcd, setEditIcd] = useState('');
  const [editDisplay, setEditDisplay] = useState('');
  const [editStatus, setEditStatus] = useState<'Active' | 'Resolved'>('Active');
  const [adding, setAdding] = useState(false);
  const [newIcd, setNewIcd] = useState('');
  const [newDisplay, setNewDisplay] = useState('');
  const [undoToast, setUndoToast] = useState<{ condition: EHRCondition; timer: ReturnType<typeof setTimeout> } | null>(null);

  const startEdit = (c: EHRCondition) => {
    setEditId(c.id);
    setEditIcd(c.icd);
    setEditDisplay(c.display);
    setEditStatus(c.status);
  };

  const saveEdit = () => {
    if (editId === null) return;
    onSetConditions(prev => prev.map(c => c.id === editId ? { ...c, icd: editIcd, display: editDisplay, status: editStatus } : c));
    setEditId(null);
  };

  const removeCondition = (id: string) => {
    const removed = conditions.find(c => c.id === id);
    if (!removed) return;
    onSetConditions(prev => prev.filter(c => c.id !== id));
    if (editId === id) setEditId(null);
    if (undoToast) clearTimeout(undoToast.timer);
    const timer = setTimeout(() => setUndoToast(null), 5000);
    setUndoToast({ condition: removed, timer });
    if (removed.source === 'raapid' && onConditionRemoved) {
      onConditionRemoved(removed);
    }
  };

  const undoRemove = () => {
    if (!undoToast) return;
    clearTimeout(undoToast.timer);
    onSetConditions(prev => [...prev, undoToast.condition]);
    if (undoToast.condition.source === 'raapid' && onConditionRestored) {
      onConditionRestored(undoToast.condition);
    }
    setUndoToast(null);
  };

  const addCondition = () => {
    if (!newIcd.trim() || !newDisplay.trim()) return;
    onSetConditions(prev => [...prev, { id: nextConditionId(), icd: newIcd.trim(), display: newDisplay.trim(), status: 'Active', onset: new Date().toISOString().slice(0, 10), source: 'ehr' }]);
    setNewIcd('');
    setNewDisplay('');
    setAdding(false);
  };

  // Bug fix #3: if the condition being edited is removed externally, cancel edit
  const editingCondition = editId ? conditions.find(c => c.id === editId) : null;
  if (editId && !editingCondition) {
    // Condition was removed while editing — clear stale edit state on next render
    setTimeout(() => setEditId(null), 0);
  }

  return (
    <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '6px' }}>
      <div style={{ background: '#F3F4F6', padding: '8px 12px', borderBottom: '1px solid #E5E7EB', borderRadius: '6px 6px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: '13px', fontWeight: 700, color: G.dark }}>Active Conditions</span>
        <span style={{ fontSize: '11px', color: '#9CA3AF' }}>{conditions.length} problems</span>
      </div>
      <div style={{ maxHeight: '260px', overflowY: 'auto', overscrollBehavior: 'contain', padding: '4px 12px' }}>
        {conditions.map((c) => (
          <div key={c.id}>
            {editId === c.id ? (
              <div style={{ padding: '6px 0', borderBottom: '1px solid #F3F4F6', display: 'flex', gap: '4px', alignItems: 'center' }}>
                <input value={editIcd} onChange={e => setEditIcd(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditId(null); }} style={{ width: '75px', fontSize: '11px', padding: '3px 5px', border: '1px solid #D1D5DB', borderRadius: '3px', fontFamily: 'monospace' }} />
                <input value={editDisplay} onChange={e => setEditDisplay(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditId(null); }} style={{ flex: 1, fontSize: '12px', padding: '3px 5px', border: '1px solid #D1D5DB', borderRadius: '3px' }} />
                <select value={editStatus} onChange={e => setEditStatus(e.target.value as 'Active' | 'Resolved')} style={{ fontSize: '11px', padding: '3px', border: '1px solid #D1D5DB', borderRadius: '3px' }}>
                  <option value="Active">Active</option>
                  <option value="Resolved">Resolved</option>
                </select>
                <button onClick={saveEdit} style={{ background: '#059669', color: 'white', border: 'none', borderRadius: '4px', padding: '3px 8px', fontSize: '11px', cursor: 'pointer', fontWeight: 700 }}>Save</button>
                <button onClick={() => setEditId(null)} style={{ background: '#F3F4F6', color: '#6B7280', border: '1px solid #E5E7EB', borderRadius: '4px', padding: '3px 8px', fontSize: '11px', cursor: 'pointer' }}>Cancel</button>
              </div>
            ) : (
              <div style={{ padding: '6px 0', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '13px', color: c.status === 'Resolved' ? '#9CA3AF' : '#111827', flex: 1, textDecoration: c.status === 'Resolved' ? 'line-through' : 'none' }}>{c.display}</span>
                <code style={{ fontSize: '11px', color: '#6B7280', background: '#F3F4F6', padding: '1px 5px', borderRadius: '3px', flexShrink: 0 }}>{c.icd}</code>
                <button onClick={() => startEdit(c)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', fontSize: '13px', padding: '0 2px' }} title="Edit">✎</button>
                <button onClick={() => removeCondition(c.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#D1D5DB', fontSize: '13px', padding: '0 2px' }} title="Remove">×</button>
              </div>
            )}
          </div>
        ))}
      </div>
      <div style={{ padding: '4px 12px' }}>
        {adding ? (
          <div style={{ padding: '6px 0', display: 'flex', gap: '4px', alignItems: 'center' }}>
            <input value={newIcd} onChange={e => setNewIcd(e.target.value)} placeholder="ICD-10" style={{ width: '75px', fontSize: '11px', padding: '3px 5px', border: '1px solid #D1D5DB', borderRadius: '3px', fontFamily: 'monospace' }} />
            <input value={newDisplay} onChange={e => setNewDisplay(e.target.value)} placeholder="Condition description" style={{ flex: 1, fontSize: '12px', padding: '3px 5px', border: '1px solid #D1D5DB', borderRadius: '3px' }} onKeyDown={e => { if (e.key === 'Enter') addCondition(); }} />
            <button onClick={addCondition} style={{ background: '#059669', color: 'white', border: 'none', borderRadius: '4px', padding: '3px 8px', fontSize: '11px', cursor: 'pointer', fontWeight: 700 }}>Add</button>
            <button onClick={() => { setAdding(false); setNewIcd(''); setNewDisplay(''); }} style={{ background: '#F3F4F6', color: '#6B7280', border: '1px solid #E5E7EB', borderRadius: '4px', padding: '3px 8px', fontSize: '11px', cursor: 'pointer' }}>Cancel</button>
          </div>
        ) : (
          <button onClick={() => setAdding(true)} style={{ width: '100%', padding: '7px 0', background: 'none', border: '1px dashed #D1D5DB', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', color: '#6B7280', marginTop: '4px', marginBottom: '4px' }}>
            + Add Condition
          </button>
        )}
        {undoToast && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 10px', background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: '4px', marginTop: '4px', marginBottom: '4px' }}>
            <span style={{ fontSize: '12px', color: '#92400E' }}>Removed <strong>{undoToast.condition.icd}</strong></span>
            <button onClick={undoRemove} style={{ background: '#B45309', color: 'white', border: 'none', borderRadius: '4px', padding: '3px 10px', fontSize: '11px', cursor: 'pointer', fontWeight: 700 }}>Undo</button>
          </div>
        )}
      </div>
    </div>
  );
}

const DOC_CAT: Record<string, { bg: string; color: string }> = {
  Lab:       { bg: '#EFF6FF', color: '#1D4ED8' },
  Imaging:   { bg: '#F0FDF4', color: '#15803D' },
  Consult:   { bg: '#FFF7ED', color: '#C2410C' },
  Procedure: { bg: '#FAF5FF', color: '#7E22CE' },
  ED:        { bg: '#FEF2F2', color: '#DC2626' },
  Discharge: { bg: '#FFFBEB', color: '#B45309' },
  Note:      { bg: '#F3F4F6', color: '#374151' },
};

type ChartDoc = { date: string; type: string; category: string; provider: string; excerpt: string };

function parseDocDate(d: string) {
  const [m, dd, y] = d.split('/');
  return y + m + dd;
}

function ChartReviewTab({ docs, notes }: { docs: readonly ChartDoc[]; notes?: readonly { date: string; type: string; provider: string; excerpt: string }[] }) {
  const allDocs: ChartDoc[] = [
    ...docs,
    ...(notes || []).map(n => ({ ...n, category: 'Note' })),
  ].sort((a, b) => parseDocDate(b.date).localeCompare(parseDocDate(a.date)));
  const [selectedIdx, setSelectedIdx] = useState(0);
  const selected = allDocs[selectedIdx];
  const cat = selected ? (DOC_CAT[selected.category] || DOC_CAT.Lab) : DOC_CAT.Lab;
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', padding: '14px', gap: '10px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <h3 style={{ fontSize: '14px', fontWeight: 700, color: G.dark, margin: 0 }}>Chart Review</h3>
        <span style={{ fontSize: '12px', color: '#9CA3AF' }}>{allDocs.length} documents</span>
      </div>
      <select
        value={selectedIdx}
        onChange={e => setSelectedIdx(Number(e.target.value))}
        style={{ width: '100%', padding: '8px 10px', fontSize: '13px', border: '1px solid #E5E7EB', borderRadius: '6px', background: 'white', color: G.dark, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}
      >
        {allDocs.map((d, i) => (
          <option key={i} value={i}>[{d.category}] {d.date} — {d.type}</option>
        ))}
      </select>
      {selected && (
        <div style={{ flex: 1, overflow: 'auto', background: 'white', border: '1px solid #E5E7EB', borderRadius: '6px' }}>
          <div style={{ background: '#F3F4F6', padding: '10px 14px', borderBottom: '2px solid #D1D5DB', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '8px', fontWeight: 700, background: cat.bg, color: cat.color }}>{selected.category}</span>
              <div>
                <div style={{ fontSize: '14px', fontWeight: 700, color: G.dark }}>{selected.type}</div>
                <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>{selected.provider}</div>
              </div>
            </div>
            <span style={{ fontSize: '12px', color: '#6B7280', fontFamily: 'monospace' }}>{selected.date}</span>
          </div>
          <div style={{ padding: '14px', fontSize: '13px', color: '#374151', lineHeight: 1.7, whiteSpace: 'pre-wrap', fontFamily: "'Courier New', Courier, monospace" }}>{selected.excerpt}</div>
        </div>
      )}
    </div>
  );
}

function OrdersTab({ orders }: { orders: typeof GENERIC_ORDERS }) {
  return (
    <div style={{ height: '100%', overflowY: 'auto', padding: '14px' }}>
      <h3 style={{ fontSize: '14px', fontWeight: 700, color: G.dark, marginBottom: '10px', borderBottom: '2px solid #E5E7EB', paddingBottom: '6px' }}>Orders</h3>
      <Table headers={['Order','Type','Status','Date','Provider']}>
        {orders.map((o, i) => (
          <tr key={i} style={{ background: i%2?'#FAFAFA':'white', borderBottom: '1px solid #F3F4F6' }}>
            <td style={{ padding: '8px 10px', fontWeight: 600, color: '#111827' }}>{o.name}</td>
            <td style={{ padding: '8px 10px', color: '#6B7280' }}>{o.type}</td>
            <td style={{ padding: '8px 10px' }}><StatusChip s={o.status} /></td>
            <td style={{ padding: '8px 10px', color: '#6B7280', fontFamily: 'monospace', fontSize: '12px' }}>{o.date}</td>
            <td style={{ padding: '8px 10px', color: '#6B7280' }}>{o.provider}</td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

function ResultsTab({ labs }: { labs: typeof GENERIC_LABS }) {
  return (
    <div style={{ height: '100%', overflowY: 'auto', padding: '14px' }}>
      <h3 style={{ fontSize: '14px', fontWeight: 700, color: G.dark, marginBottom: '10px', borderBottom: '2px solid #E5E7EB', paddingBottom: '6px' }}>Lab Results</h3>
      <Table headers={['Test','Result','Reference','Flag','Date','Note']}>
        {labs.map((lab, i) => (
          <tr key={i} style={{ background: lab.flag?'#FFF7ED':i%2?'#FAFAFA':'white', borderBottom: '1px solid #F3F4F6' }}>
            <td style={{ padding: '8px 10px', fontWeight: 600, color: '#111827' }}>{lab.name}</td>
            <td style={{ padding: '8px 10px', fontWeight: 700, color: lab.flag==='H'?'#B45309':lab.flag==='L'?'#1D4ED8':'#111827' }}>{lab.value}</td>
            <td style={{ padding: '8px 10px', color: '#9CA3AF', fontSize: '12px' }}>{lab.ref}</td>
            <td style={{ padding: '8px 10px' }}>{lab.flag && <span style={{ padding: '2px 7px', borderRadius: '8px', fontSize: '11px', fontWeight: 700, background: lab.flag==='H'?'#FEF3C7':'#DBEAFE', color: lab.flag==='H'?'#B45309':'#1D4ED8' }}>{lab.flag}</span>}</td>
            <td style={{ padding: '8px 10px', color: '#6B7280', fontFamily: 'monospace', fontSize: '12px' }}>{lab.date}</td>
            <td style={{ padding: '8px 10px', color: '#9CA3AF', fontSize: '12px', fontStyle: 'italic' }}>{lab.note}</td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

interface NotesTabProps {
  notes: typeof GENERIC_NOTES;
  addendumEntries: AddendumEntry[];
  addendumFinalized: boolean;
  attestationInfo: { by: string; at: string; specialty: string | null } | null;
  providerName: string;
  providerTitle: string;
  patientMrn: string;
  encounterType: string;
}

function NotesTab({ notes, addendumEntries, addendumFinalized, attestationInfo, providerName, providerTitle, patientMrn, encounterType }: NotesTabProps) {
  const today = new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  const hasAddendum = addendumEntries.length > 0;
  const generatedText = hasAddendum ? generateAddendumText(addendumEntries, providerName, providerTitle, patientMrn, encounterType, addendumFinalized, attestationInfo) : '';
  const addendumNote = hasAddendum ? { date: today, type: addendumFinalized ? 'RAAPID Risk Adjustment Addendum (Signed)' : 'RAAPID Risk Adjustment Addendum (Draft)', provider: providerName, excerpt: generatedText } : null;
  const allNotes = addendumNote ? [addendumNote, ...notes] : [...notes];
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const prevEntryCountRef = useRef(addendumEntries.length);

  useEffect(() => { setSelectedIdx(0); setEditing(false); }, [hasAddendum]);
  useEffect(() => {
    if (editing && addendumEntries.length !== prevEntryCountRef.current) setEditing(false);
    prevEntryCountRef.current = addendumEntries.length;
  }, [addendumEntries.length, editing]);

  const selected = allNotes[selectedIdx];
  const isAddendumSelected = hasAddendum && selectedIdx === 0;
  const canEdit = isAddendumSelected && !addendumFinalized;
  const displayText = editing ? editText : selected?.excerpt ?? '';

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', padding: '14px', gap: '10px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <h3 style={{ fontSize: '14px', fontWeight: 700, color: G.dark, margin: 0 }}>Clinical Notes</h3>
        <span style={{ fontSize: '12px', color: '#9CA3AF' }}>{allNotes.length} notes</span>
      </div>
      <select
        value={selectedIdx}
        onChange={e => { setSelectedIdx(Number(e.target.value)); setEditing(false); }}
        style={{ width: '100%', padding: '8px 10px', fontSize: '13px', border: '1px solid #E5E7EB', borderRadius: '6px', background: 'white', color: G.dark, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}
      >
        {allNotes.map((n, i) => (
          <option key={i} value={i}>{hasAddendum && i === 0 ? '⬥ ' : ''}{n.date} — {n.type}</option>
        ))}
      </select>
      {selected && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'white', border: '1px solid #E5E7EB', borderLeft: `4px solid ${isAddendumSelected ? (addendumFinalized ? '#059669' : '#3B82F6') : G.accent}`, borderRadius: '0 6px 6px 0' }}>
          <div style={{ padding: '12px 14px', borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0, background: isAddendumSelected ? (addendumFinalized ? '#F0FDF4' : '#EFF6FF') : 'white' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {isAddendumSelected && <span style={{ fontSize: '14px' }}>{addendumFinalized ? '✓' : '📋'}</span>}
              <div>
                <div style={{ fontWeight: 700, fontSize: '14px', color: G.dark }}>{selected.type}</div>
                <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>{selected.provider}</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              {canEdit && !editing && (
                <button onClick={() => { setEditText(generatedText); setEditing(true); }} style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '4px', padding: '4px 12px', fontSize: '12px', color: '#1D4ED8', cursor: 'pointer', fontWeight: 600 }}>Edit Draft</button>
              )}
              {editing && (
                <>
                  <button onClick={() => setEditText(generatedText)} style={{ background: '#F3F4F6', border: '1px solid #E5E7EB', borderRadius: '4px', padding: '4px 10px', fontSize: '11px', color: '#6B7280', cursor: 'pointer' }}>Reset</button>
                  <button onClick={() => setEditing(false)} style={{ background: '#059669', border: 'none', borderRadius: '4px', padding: '4px 12px', fontSize: '12px', color: 'white', cursor: 'pointer', fontWeight: 600 }}>Done</button>
                </>
              )}
              {addendumFinalized && isAddendumSelected && <span style={{ fontSize: '11px', color: '#059669', fontWeight: 600, background: '#ECFDF5', padding: '2px 8px', borderRadius: '8px', border: '1px solid #A7F3D0' }}>Locked</span>}
              <span style={{ fontSize: '13px', color: '#9CA3AF', fontFamily: 'monospace' }}>{selected.date}</span>
            </div>
          </div>
          {editing ? (
            <textarea value={editText} onChange={e => setEditText(e.target.value)} style={{ flex: 1, padding: '14px', fontSize: '13px', color: '#374151', lineHeight: 1.7, fontFamily: "'Courier New', Courier, monospace", border: 'none', outline: 'none', resize: 'none', background: '#FEFCE8' }} />
          ) : (
            <div style={{ flex: 1, overflow: 'auto', padding: '14px', fontSize: '13px', color: '#374151', lineHeight: 1.7, whiteSpace: 'pre-wrap', fontFamily: isAddendumSelected ? "'Courier New', Courier, monospace" : 'inherit' }}>{displayText}</div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Shared primitives ────────────────────────────────────────────────────────

function Card({ title, sub, children, maxContentHeight = 260 }: { title: string; sub: string; children: React.ReactNode; maxContentHeight?: number }) {
  return (
    <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '6px', display: 'flex', flexDirection: 'column' }}>
      <div style={{ background: '#F3F4F6', padding: '8px 12px', borderBottom: '1px solid #E5E7EB', borderRadius: '6px 6px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
        <span style={{ fontSize: '13px', fontWeight: 700, color: G.dark }}>{title}</span>
        <span style={{ fontSize: '11px', color: '#9CA3AF' }}>{sub}</span>
      </div>
      <div style={{ padding: '8px 12px', overflowY: 'auto', overscrollBehavior: 'contain', maxHeight: `${maxContentHeight}px` }}>{children}</div>
    </div>
  );
}

function VR({ label, value, alert }: { label: string; value: string; alert?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #F9FAFB' }}>
      <span style={{ fontSize: '12px', color: '#6B7280' }}>{label}</span>
      <span style={{ fontSize: '13px', fontWeight: 600, color: alert ? '#B45309' : '#111827' }}>{value}</span>
    </div>
  );
}

function Table({ headers, children }: { headers: string[]; children: React.ReactNode }) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
      <thead><tr style={{ background: '#F3F4F6' }}>{headers.map(h => <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: G.dark, borderBottom: '2px solid #D1D5DB', fontSize: '12px' }}>{h}</th>)}</tr></thead>
      <tbody>{children}</tbody>
    </table>
  );
}

function StatusChip({ s }: { s: string }) {
  const bg = s==='Completed'?'#ECFDF5':s==='Pending'?'#FFFBEB':'#EFF6FF';
  const color = s==='Completed'?'#059669':s==='Pending'?'#B45309':'#1D4ED8';
  return <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 700, background: bg, color }}>{s}</span>;
}

function Sep() { return <span style={{ width: '1px', height: '14px', background: G.border }} />; }
const ghost: React.CSSProperties = { background: 'transparent', border: 'none', color: G.text, cursor: 'pointer', padding: '4px 8px', borderRadius: '4px', fontSize: '14px', display: 'flex', alignItems: 'center' };

function ProviderPicker({ theme, onSelect, onBack }: { theme: typeof G; onSelect: (p: MockProvider) => void; onBack: () => void }) {
  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', background: theme.bg }}>
      <div style={{ background: theme.dark, padding: '14px 20px', display: 'flex', alignItems: 'center', gap: '12px', borderBottom: `2px solid ${theme.nav}` }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: theme.text, cursor: 'pointer', display: 'flex', alignItems: 'center' }} title="Back to home"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg></button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '28px', height: '28px', borderRadius: '6px', background: theme.accent, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ color: 'white', fontSize: '14px', fontWeight: 900 }}>+</span>
          </div>
          <span style={{ color: 'white', fontWeight: 700, fontSize: '15px' }}>Clinical Workstation</span>
          <span style={{ color: theme.text, fontSize: '11px' }}>EHR</span>
        </div>
      </div>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px' }}>
        <div style={{ width: '100%', maxWidth: '480px' }}>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: theme.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <span style={{ color: 'white', fontSize: '22px', fontWeight: 900 }}>+</span>
            </div>
            <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#111827', margin: '0 0 6px' }}>Select Provider</h2>
            <p style={{ fontSize: '13px', color: '#6B7280', margin: 0 }}>Choose your provider profile to begin this session</p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {MOCK_PROVIDERS.map((p) => (
              <button
                key={p.initials}
                onClick={() => onSelect(p)}
                style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 16px', background: 'white', border: '1px solid #E5E7EB', borderRadius: '10px', cursor: 'pointer', textAlign: 'left', transition: 'border-color 0.15s, box-shadow 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = theme.accent; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.accent}20`; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#E5E7EB'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={{ width: '42px', height: '42px', borderRadius: '50%', background: theme.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '14px', fontWeight: 700, flexShrink: 0 }}>{p.initials}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>{p.name}</div>
                  <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>{p.title}</div>
                </div>
                <span style={{ color: '#D1D5DB', fontSize: '18px' }}>→</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
